import { useEffect, useState } from 'react';

/**
 * Minimal, GDPR-friendly cookie banner.
 * - Sits fixed at the bottom; does not block page interaction until dismissed
 * - Persists decision in localStorage (ludy_cookie_consent: "accepted" | "rejected")
 * - Only shows when no decision has been recorded yet
 * - Implicit consent is NOT granted; Sentry Session Replay + any future analytics
 *   should gate themselves on getCookieConsent() === 'accepted'.
 */
export function getCookieConsent() {
  try {
    return localStorage.getItem('ludy_cookie_consent');
  } catch {
    return null;
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Slight delay so banner appears after initial paint, not during first frame
    const t = setTimeout(() => {
      if (!getCookieConsent()) setVisible(true);
    }, 400);
    return () => clearTimeout(t);
  }, []);

  const decide = (value) => {
    try { localStorage.setItem('ludy_cookie_consent', value); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:max-w-md z-[9999] rounded-2xl border border-border bg-card shadow-2xl p-5"
      role="region"
      aria-label="Cookie consent"
    >
      <p className="text-sm font-semibold text-foreground mb-1.5">
        We use cookies
      </p>
      <p className="text-xs text-muted-foreground leading-relaxed mb-4">
        Ludy uses essential cookies to keep you signed in, and optional cookies for analytics
        and product-improvement (Sentry error monitoring + session replay). You can accept or
        reject these non-essential cookies. You can change your choice any time via the footer.
        See our{' '}
        <a href="/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </a>
        .
      </p>
      <div className="flex gap-2">
        <button
          onClick={() => decide('rejected')}
          className="flex-1 px-3 py-2 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:bg-secondary/60 transition-colors"
          data-testid="cookie-consent-reject"
        >
          Reject non-essential
        </button>
        <button
          onClick={() => decide('accepted')}
          className="flex-1 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors"
          data-testid="cookie-consent-accept"
        >
          Accept all
        </button>
      </div>
    </div>
  );
}
